'use client';

import React from 'react';
import { Box, cx } from '@terra/ui-styled-system';
import { Spinner } from '../../../components/spinner';
import { renderChatMessagePart } from './virtualized-chat-message-part';
import { ChatUserMessageItem } from './ChatUserMessageItem';
import { FeedbackActions } from '../feedback/FeedbackActions';
import type { AgentInfo, UserInfo } from '../types';
import type { ProcessedMessage } from '../utils/message-processing';
import { buildToolResultsById } from '../utils/message-processing';
import {
  buildChatRenderItems,
  buildConsolidatedSummary,
  isObjectResultPart,
  isRunningPart,
  type ChatRenderItem,
  type RenderablePart,
  type ConsolidationSurface,
} from './chat-message-list-grouping';
import { ConsolidatedToolGroup } from './ConsolidatedToolGroup';
import { hasRenderableNode } from './chat-renderability';
import { BackgroundUpdateSeparator } from './BackgroundUpdateSeparator';
import { SubAgentExecutionGroupView } from './chat-message-list-subagent-render';
import type { MessageListCallbacks } from './ChatMessageList';
import { useSiblingHITLCallbacks } from '../hooks/useSiblingHITLCallbacks';
import { withPinnedPlanHeader } from './chat-message-list-helpers';
import { useChatVirtualizer } from '../hooks/useChatVirtualizer';
import { useVirtualizedChatScroll } from '../hooks/useVirtualizedChatScroll';
import {
  getConsolidatedGroupExpansionKey,
  getStableKeyForRenderItem,
} from '../utils/chat-render-item-keys';
import {
  findChatRenderItemIndexForToolCallId,
  findLatestTurnPendingHITLRenderItemIndex,
} from './chat-render-item-tool-call';
import {
  DIMMED_CHAT_ROW_OPACITY,
  findEditingRenderItemIndex,
  isRenderItemDimmedByEditing,
} from './chat-message-list-editing';

import {
  chatLaterTurnRowStyles,
  chatRowStyles,
  chatUserRowStyles,
} from './chat-message-list-row-styles';

type VirtualizedChatMessageListProps = {
  processedMessages: ProcessedMessage[];
  stableIsLoading: boolean;
  isFeedbackEnabled?: boolean;
  agent?: AgentInfo;
  user?: UserInfo;
  noAssistantHeader?: boolean;
  callbacks: MessageListCallbacks;
  scrollElementRef: React.RefObject<HTMLDivElement | null>;
  onLoadMore?: () => Promise<void>;
  hasMoreMessages?: boolean;
  isLoadingMoreMessages?: boolean;
  pinnedHeaderContent?: React.ReactNode;
  scrollSessionId?: string;
  isScrollerActive?: boolean;
};

export type VirtualizedChatMessageListRef = {
  scrollToEnd: () => void;
  isAtEnd: (threshold?: number) => boolean;
  scrollToIndex: (
    index: number,
    options?: { align?: 'start' | 'center' | 'end' | 'auto' },
  ) => void;
  scrollToToolCallId: (toolCallId: string) => boolean;
};
export const VirtualizedChatMessageList = React.forwardRef<
  VirtualizedChatMessageListRef,
  VirtualizedChatMessageListProps
>(function VirtualizedChatMessageList(
  {
    processedMessages,
    stableIsLoading,
    isFeedbackEnabled,
    agent,
    user,
    noAssistantHeader,
    callbacks,
    scrollElementRef,
    onLoadMore,
    hasMoreMessages = false,
    isLoadingMoreMessages = false,
    pinnedHeaderContent,
    scrollSessionId,
    isScrollerActive = true,
  },
  ref,
) {
  const toolResultsById = React.useMemo(
    () => buildToolResultsById(processedMessages),
    [processedMessages],
  );
  const { buildCallbacksForMessage, buildCallbacksForItems } =
    useSiblingHITLCallbacks({ callbacks, processedMessages, toolResultsById });
  const renderRenderablePart = ({
    item,
    nestedContent,
    nestedSummary,
    isNestedInSubAgent,
    forceExpanded,
    isGroupRunning,
    callbacksOverride,
  }: {
    item: RenderablePart;
    nestedContent?: React.ReactNode;
    nestedSummary?: string;
    isNestedInSubAgent?: boolean;
    forceExpanded?: boolean;
    isGroupRunning?: boolean;
    callbacksOverride?: MessageListCallbacks;
  }) =>
    renderChatMessagePart({
      processedPart: item.processedPart,
      processedMessage: item.processedMessage,
      callbacks:
        callbacksOverride ?? buildCallbacksForMessage(item.processedMessage),
      noAssistantHeader: item.noAssistantHeader,
      agent,
      autoCollapseToolCalls: item.autoCollapseToolCalls,
      isFinalAssistantOutcome:
        item.processedPart.kind === 'assistant-text' &&
        item.processedPart.part.type !== 'reasoning' &&
        !stableIsLoading &&
        item.processedMessage.isLastAssistantMessage,
      isChatLoading: stableIsLoading,
      nestedContent,
      nestedSummary,
      isNestedInSubAgent,
      forceExpanded,
      isGroupRunning,
      toolResultsById,
    });
  const renderConsolidatedGroup = (
    items: RenderablePart[],
    options?: {
      isNestedInSubAgent?: boolean;
      callbacksOverride?: MessageListCallbacks;
      surface?: ConsolidationSurface;
    },
  ): React.ReactNode => {
    const surface = options?.surface ?? 'explored';
    const childContent = items
      .map((item) => ({
        item,
        rendered: renderRenderablePart({
          item,
          isNestedInSubAgent: options?.isNestedInSubAgent,
          forceExpanded: isObjectResultPart(item.processedPart),
          callbacksOverride: options?.callbacksOverride,
        }),
      }))
      .filter(({ rendered }) => hasRenderableNode(rendered))
      .map(({ item, rendered }) => (
        <Box
          key={item.processedPart.key}
          width="100%"
          _empty={{ display: 'none' }}
        >
          {rendered}
        </Box>
      ));

    return (
      <ConsolidatedToolGroup
        childContent={childContent}
        summary={buildConsolidatedSummary(items, surface)}
        isRunning={items.some((item) => isRunningPart(item.processedPart))}
        surface={surface}
        expansionKey={getConsolidatedGroupExpansionKey({ items, surface })}
      />
    );
  };

  const renderSubAgentGroup = (
    group: Parameters<typeof SubAgentExecutionGroupView>[0]['group'],
  ) => (
    <SubAgentExecutionGroupView
      group={group}
      renderRenderablePart={renderRenderablePart}
      renderConsolidatedGroup={renderConsolidatedGroup}
      buildCallbacksForItems={buildCallbacksForItems}
    />
  );

  const hasPinnedHeader = Boolean(pinnedHeaderContent);
  const renderItems = React.useMemo(() => {
    const baseItems = buildChatRenderItems({
      processedMessages,
      stableIsLoading,
      isFeedbackEnabled,
      noAssistantHeader,
    });

    return hasPinnedHeader ? withPinnedPlanHeader(baseItems) : baseItems;
  }, [
    processedMessages,
    stableIsLoading,
    isFeedbackEnabled,
    noAssistantHeader,
    hasPinnedHeader,
  ]);
  const stickyIndex = React.useMemo(
    () =>
      hasPinnedHeader
        ? renderItems.findIndex((item) => item.kind === 'pinned-plan-header')
        : -1,
    [renderItems, hasPinnedHeader],
  );
  const { virtualizer, activeStickyIndexRef } = useChatVirtualizer({
    items: renderItems,
    scrollElementRef,
    stickyIndex,
  });
  const totalSize = virtualizer.getTotalSize();
  const virtualItems = virtualizer.getVirtualItems();
  const pendingHitl = React.useMemo(() => {
    const index = findLatestTurnPendingHITLRenderItemIndex(renderItems);

    return index < 0
      ? null
      : { index, key: getStableKeyForRenderItem(renderItems[index]) };
  }, [renderItems]);
  const chatScroll = useVirtualizedChatScroll({
    renderItemsLength: renderItems.length,
    firstRenderItemKey: renderItems[0]
      ? getStableKeyForRenderItem(renderItems[0])
      : null,
    pendingHitlIndex: pendingHitl?.index ?? null,
    pendingHitlKey: pendingHitl?.key ?? null,
    stableIsLoading,
    hasPendingHITL: pendingHitl !== null,
    totalSize,
    virtualizer,
    scrollElementRef,
    onLoadMore,
    hasMoreMessages,
    isLoadingMoreMessages,
    scrollSessionId,
    isScrollerActive,
  });

  const scrollToIndex = React.useCallback(
    (
      index: number,
      options?: { align?: 'start' | 'center' | 'end' | 'auto' },
    ) => {
      virtualizer.scrollToIndex(index, options);
    },
    [virtualizer],
  );
  const scrollToToolCallId = React.useCallback(
    (toolCallId: string) => {
      const index = findChatRenderItemIndexForToolCallId({
        items: renderItems,
        toolCallId,
      });

      if (index < 0) {
        return false;
      }

      virtualizer.scrollToIndex(index, { align: 'auto' });

      return true;
    },
    [renderItems, virtualizer],
  );

  React.useImperativeHandle(
    ref,
    () => ({
      ...chatScroll,
      scrollToIndex,
      scrollToToolCallId,
    }),
    [chatScroll, scrollToIndex, scrollToToolCallId],
  );
  const editingIndex = findEditingRenderItemIndex({
    renderItems,
    editingMessageId: callbacks.inlineEdit?.editingMessageId,
  });
  const renderChatItem = (item: ChatRenderItem): React.ReactNode => {
    switch (item.kind) {
      case 'user-message':
        return (
          <ChatUserMessageItem
            processedMessage={item.processedMessage}
            user={user}
            callbacks={callbacks}
          />
        );

      case 'part':
        return renderRenderablePart({
          item: item.item,
          forceExpanded: isObjectResultPart(item.item.processedPart),
        });
      case 'consolidated-group':
        return renderConsolidatedGroup(item.items, {
          surface: item.surface,
        });
      case 'sub-agent-group':
        return renderSubAgentGroup(item.group);
      case 'feedback':
        return (
          <FeedbackActions traceId={item.processedMessage.traceId ?? ''} />
        );
      case 'background-update-separator':
        return (
          <BackgroundUpdateSeparator
            agentType={item.agentType}
            stopped={item.stopped}
          />
        );
      case 'pinned-plan-header':
        return <Box bg="bg.default">{pinnedHeaderContent}</Box>;
      default:
        return null;
    }
  };

  return (
    <>
      {isLoadingMoreMessages && (
        <Box display="flex" justifyContent="center" py={2} width="100%">
          <Spinner size="sm" />
        </Box>
      )}
      <div
        style={{
          height: totalSize,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualItems.map((virtualRow) => {
          const renderItem = renderItems[virtualRow.index];
          const isPinnedHeader = renderItem?.kind === 'pinned-plan-header';
          const isActiveSticky =
            isPinnedHeader && activeStickyIndexRef.current === virtualRow.index;
          const content = renderItem ? renderChatItem(renderItem) : null;
          const isUserRow = renderItem?.kind === 'user-message';
          const rowOpacity = isRenderItemDimmedByEditing({
            index: virtualRow.index,
            editingIndex,
          })
            ? DIMMED_CHAT_ROW_OPACITY
            : 1;

          return (
            <div
              key={virtualRow.key}
              ref={virtualizer.measureElement}
              data-index={virtualRow.index}
              style={
                isActiveSticky
                  ? {
                      position: 'sticky',
                      top: 0,
                      left: 0,
                      width: '100%',
                      zIndex: 2,
                      opacity: rowOpacity,
                    }
                  : {
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      transform: `translateY(${virtualRow.start}px)`,
                      opacity: rowOpacity,
                      transition: 'opacity 0.15s ease',
                      ...(isPinnedHeader ? { zIndex: 2 } : {}),
                    }
              }
            >
              <div
                className={cx(
                  isUserRow ? chatUserRowStyles : chatRowStyles,
                  isUserRow && virtualRow.index > 0 && chatLaterTurnRowStyles,
                )}
              >
                {content}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
});
